import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useSubscriptions, useWallet, QUERY_KEYS } from "@/hooks/use-queries";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  CreditCard,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Settings,
  Zap,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { PageAbout } from "@/components/page-about";

const subscriptionFormSchema = z.object({
  planTier: z.string().min(1, "Please select a plan"),
  amount: z.coerce.number().min(500, "Minimum $5.00").max(2500, "Maximum $25.00"),
  frequency: z.string().min(1, "Please select a frequency"),
  autopayEnabled: z.boolean().default(true),
  paymentDayOfMonth: z.coerce.number().min(1).max(28).optional(),
  paymentDayOfWeek: z.coerce.number().min(0).max(6).optional(),
});

type SubscriptionFormValues = z.infer<typeof subscriptionFormSchema>;

interface Subscription {
  id: string;
  userId: string;
  walletId: string;
  planTier: string;
  monthlyAmount: number;
  frequency: string;
  status: string;
  autopayEnabled: boolean;
  stripeSubscriptionId?: string;
  nextPaymentDate?: string;
  pausedAt?: string;
  pauseReason?: string;
  totalContributed: number;
  paymentsCount: number;
  createdAt: string;
}

interface SubscriptionPayment {
  id: string;
  subscriptionId: string;
  amount: number;
  status: string;
  stripePaymentId?: string;
  failureReason?: string;
  createdAt: string;
  processedAt?: string;
}

const planTiers = [
  { value: "bronze", label: "Bronze", min: 5, max: 10, description: "Basic support for essential healthcare" },
  { value: "silver", label: "Silver", min: 10, max: 15, description: "Enhanced funding with priority access" },
  { value: "gold", label: "Gold", min: 15, max: 25, description: "Full funding with comprehensive support" },
];

const frequencies = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
];

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function getStatusBadge(status: string) {
  switch (status) {
    case "active":
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle className="w-3 h-3 mr-1" /> Active</Badge>;
    case "paused":
      return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20"><Pause className="w-3 h-3 mr-1" /> Paused</Badge>;
    case "cancelled":
      return <Badge className="bg-red-500/10 text-red-600 border-red-500/20"><AlertCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

export default function SubscriptionsPage() {
  usePageTitle("Pre-Care Subscriptions", "Manage recurring micro-contributions for proactive healthcare savings with tiered subscription plans.");
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false);

  const { data: subscriptions, isLoading } = useSubscriptions();
  const { data: wallet } = useWallet();

  const activeSubscription = subscriptions?.find(s => s.status === "active" || s.status === "paused");

  const { data: payments, isLoading: paymentsLoading } = useQuery<SubscriptionPayment[]>({
    queryKey: ["/api/subscriptions", activeSubscription?.id, "payments"],
    enabled: !!activeSubscription,
  });

  const createSubscriptionMutation = useMutation({
    mutationFn: async (data: SubscriptionFormValues) => {
      const body = {
        planId: data.planTier,
        planTier: data.planTier,
        frequency: data.frequency,
        walletId: wallet?.id,
        amount: data.amount,
        autopayEnabled: data.autopayEnabled,
        paymentDayOfMonth: data.paymentDayOfMonth,
      };
      const response = await apiRequest("POST", "/api/subscriptions", body);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || "Failed to create subscription");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptions });
      setIsCreateDialogOpen(false);
      toast({
        title: "Subscription created successfully",
        description: "Your pre-care subscription is now active.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create subscription",
        variant: "destructive",
      });
    },
  });

  const pauseSubscriptionMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await apiRequest("POST", `/api/subscriptions/${id}/pause`, { reason });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptions });
      setIsPauseDialogOpen(false);
      toast({
        title: "Subscription Paused",
        description: "Your subscription has been paused. Resume anytime.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to pause subscription",
        variant: "destructive",
      });
    },
  });

  const resumeSubscriptionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/subscriptions/${id}/resume`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptions });
      toast({
        title: "Subscription Resumed",
        description: "Your subscription is now active again.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to resume subscription",
        variant: "destructive",
      });
    },
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/subscriptions/${id}/cancel`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptions });
      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription",
        variant: "destructive",
      });
    },
  });

  const form = useForm<SubscriptionFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(subscriptionFormSchema),
    defaultValues: {
      planTier: "",
      amount: 1000,
      frequency: "monthly",
      autopayEnabled: true,
      paymentDayOfMonth: 15,
    },
  });

  const selectedPlanTier = form.watch("planTier");
  const selectedFrequency = form.watch("frequency");

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 md:grid-cols-3">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageAbout
        title="Pre-Care Subscriptions"
        description="Build your healthcare savings proactively with automatic micro-contributions. Set up recurring payments to consistently grow your available funds before you need it."
        features={[
          "Bronze, Silver, and Gold tiers with different contribution levels ($5-$25)",
          "Choose weekly, bi-weekly, or monthly payment frequency",
          "Automatic payments from your preferred payment method",
          "Pause or resume subscriptions anytime without penalty",
          "All contributions go directly to your health wallet"
        ]}
        tips={[
          "Start with the Bronze tier if you're new - you can upgrade anytime",
          "Set payment dates to align with your payday for easier budgeting",
          "Pause your subscription during financial hardship rather than canceling"
        ]}
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold" data-testid="text-page-title">Pre-Care Subscriptions</h1>
          <p className="text-muted-foreground mt-1">
            Build your healthcare savings with automatic micro-contributions ($5-$25/month)
          </p>
        </div>
        {!activeSubscription && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-create-subscription">
                <Plus className="w-4 h-4 mr-2" />
                Start Subscription
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Pre-Care Subscription</DialogTitle>
                <DialogDescription>
                  Choose your plan tier and contribution amount. All contributions go directly to your HealthWallet.
                </DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit((data) => createSubscriptionMutation.mutate(data))} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="planTier"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Plan Tier</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-plan-tier">
                              <SelectValue placeholder="Select a plan" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {planTiers.map((tier) => (
                              <SelectItem key={tier.value} value={tier.value}>
                                <div className="flex flex-col">
                                  <span className="font-medium">{tier.label}</span>
                                  <span className="text-xs text-muted-foreground">${tier.min}-${tier.max}/mo</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedPlanTier && (
                          <FormDescription>
                            {planTiers.find(t => t.value === selectedPlanTier)?.description}
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => {
                      const dollarValue = (field.value || 0) / 100;
                      return (
                        <FormItem>
                          <FormLabel>Monthly Amount</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                              <Input
                                type="number"
                                min={5}
                                max={25}
                                step={1}
                                value={dollarValue}
                                onChange={(e) => field.onChange(Math.round(parseFloat(e.target.value || "0") * 100))}
                                className="pl-7"
                                data-testid="input-amount"
                              />
                            </div>
                          </FormControl>
                          <FormDescription>
                            {formatCents(field.value || 0)} per {selectedFrequency || "month"}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="frequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Frequency</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-frequency">
                              <SelectValue placeholder="Select frequency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {frequencies.map((freq) => (
                              <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {selectedFrequency === "monthly" && (
                    <FormField
                      control={form.control}
                      name="paymentDayOfMonth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Payment Day of Month</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              max={28}
                              {...field}
                              data-testid="input-payment-day"
                            />
                          </FormControl>
                          <FormDescription>Day 1-28 to align with your paycheck</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="autopayEnabled"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-4">
                        <div>
                          <FormLabel>Enable Autopay</FormLabel>
                          <FormDescription>
                            Automatically process payments on your schedule
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="switch-autopay"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <div className="p-3 rounded-md bg-muted/50 text-xs text-muted-foreground space-y-1" data-testid="text-subscription-not-insurance">
                    <p>Pre-care subscriptions are voluntary savings mechanisms, not insurance products, health benefit plans, or coverage programs. HealthWallet provides technology services only. All funds are processed and held by Stripe, Inc.</p>
                    <p>By subscribing, you agree to our <a href="/terms" className="underline" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" className="underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={createSubscriptionMutation.isPending}
                    data-testid="button-submit-subscription"
                  >
                    {createSubscriptionMutation.isPending ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Start Subscription
                  </Button>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Current Plan</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {activeSubscription ? (
              <div>
                <div className="text-2xl font-bold capitalize">{activeSubscription.planTier}</div>
                <p className="text-xs text-muted-foreground">
                  {formatCents(activeSubscription.monthlyAmount)} / {activeSubscription.frequency}
                </p>
              </div>
            ) : (
              <div className="text-2xl font-bold text-muted-foreground">No Active Plan</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {activeSubscription ? (
              <div className="flex items-center gap-2">
                {getStatusBadge(activeSubscription.status)}
              </div>
            ) : (
              <Badge variant="secondary">None</Badge>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Next Payment</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {activeSubscription?.nextPaymentDate ? (
              <div>
                <div className="text-2xl font-bold">
                  {format(new Date(activeSubscription.nextPaymentDate), "MMM d")}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatCents(activeSubscription.monthlyAmount)}
                </p>
              </div>
            ) : (
              <div className="text-2xl font-bold text-muted-foreground">-</div>
            )}
          </CardContent>
        </Card>
      </div>

      {activeSubscription && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Subscription Management</CardTitle>
                <CardDescription>Control your pre-care subscription settings</CardDescription>
              </div>
              <div className="flex gap-2">
                {activeSubscription.status === "active" ? (
                  <Dialog open={isPauseDialogOpen} onOpenChange={setIsPauseDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="button-pause-subscription">
                        <Pause className="w-4 h-4 mr-2" />
                        Pause
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Pause Subscription</DialogTitle>
                        <DialogDescription>
                          Need a break? You can pause your subscription anytime and resume when ready.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Your available funds will be preserved. No payments will be processed while paused.
                        </p>
                        <Button
                          onClick={() => pauseSubscriptionMutation.mutate({ 
                            id: activeSubscription.id, 
                            reason: "User requested pause" 
                          })}
                          disabled={pauseSubscriptionMutation.isPending}
                          className="w-full"
                          data-testid="button-confirm-pause"
                        >
                          {pauseSubscriptionMutation.isPending ? "Pausing..." : "Confirm Pause"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : activeSubscription.status === "paused" && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => resumeSubscriptionMutation.mutate(activeSubscription.id)}
                    disabled={resumeSubscriptionMutation.isPending}
                    data-testid="button-resume-subscription"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {resumeSubscriptionMutation.isPending ? "Resuming..." : "Resume"}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => cancelSubscriptionMutation.mutate(activeSubscription.id)}
                  disabled={cancelSubscriptionMutation.isPending}
                  className="text-destructive"
                  data-testid="button-cancel-subscription"
                >
                  {cancelSubscriptionMutation.isPending ? "Cancelling..." : "Cancel"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Plan Details</h4>
                <div className="grid gap-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tier</span>
                    <span className="capitalize font-medium">{activeSubscription.planTier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-medium">{formatCents(activeSubscription.monthlyAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Frequency</span>
                    <span className="capitalize font-medium">{activeSubscription.frequency}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Payment Settings</h4>
                <div className="grid gap-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Autopay</span>
                    <Badge variant={activeSubscription.autopayEnabled ? "default" : "secondary"}>
                      {activeSubscription.autopayEnabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Started</span>
                    <span className="font-medium">
                      {format(new Date(activeSubscription.createdAt), "MMM d, yyyy")}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Contribution Stats</h4>
                <div className="grid gap-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Contributed</span>
                    <span className="font-medium" data-testid="text-total-contributed">{formatCents(activeSubscription.totalContributed)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payments Made</span>
                    <span className="font-medium" data-testid="text-payments-count">{activeSubscription.paymentsCount}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>Track your subscription payments</CardDescription>
        </CardHeader>
        <CardContent>
          {paymentsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : payments && payments.length > 0 ? (
            <div className="space-y-2">
              {payments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                  data-testid={`payment-row-${payment.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      payment.status === "completed" 
                        ? "bg-green-500/10" 
                        : payment.status === "failed" 
                        ? "bg-red-500/10" 
                        : "bg-yellow-500/10"
                    }`}>
                      {payment.status === "completed" ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : payment.status === "failed" ? (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      ) : (
                        <Clock className="w-4 h-4 text-yellow-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{formatCents(payment.amount)}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(payment.createdAt), "MMM d, yyyy h:mm a")}
                      </p>
                    </div>
                  </div>
                  <Badge variant={
                    payment.status === "completed" ? "default" :
                    payment.status === "failed" ? "destructive" : "secondary"
                  }>
                    {payment.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No payment history yet</p>
              <p className="text-sm">Payments will appear here once processed</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Why Pre-Care Subscriptions?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <h4 className="font-medium">Build Your Safety Net</h4>
              <p className="text-sm text-muted-foreground">
                Small, consistent contributions add up to significant savings for healthcare needs.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Flexible & Pause Anytime</h4>
              <p className="text-sm text-muted-foreground">
                Life happens. Pause your subscription during tough times and resume when ready.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Earn Rewards</h4>
              <p className="text-sm text-muted-foreground">
                Active subscribers earn bonus credits and early access to community benefits.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
